import React, { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../../utils/api';

// HealthCheckPanel — admin view of liveness + correctness for four strategies:
// OSUPA, NUIT, ANYANWU, WURA.
// Reads /strategies/api/admin/health/overview/ (preferred single call).
// Falls back to parallel calls to /strategies/api/admin/health/strategies/latest
// and /strategies/api/admin/health/correctness/ if the overview 404s.

const REFRESH_MS = 20000;

const STATUS_STYLE = {
  HEALTHY: { bg: 'var(--tv-bull-tint)', border: 'var(--tv-bull)', fg: 'var(--tv-bull)' },
  STALE:   { bg: 'var(--tv-gold-tint)', border: 'var(--tv-gold)', fg: 'var(--tv-gold)' },
  DOWN:    { bg: 'var(--tv-bear-tint)', border: 'var(--tv-bear)', fg: 'var(--tv-bear)' },
  PAUSED:  { bg: 'var(--tv-blue-tint)', border: 'var(--tv-blue)', fg: 'var(--tv-blue)' },
};

const VERDICT_STYLE = {
  MATCH:       { bg: 'var(--tv-bull-tint)', border: 'var(--tv-bull)', fg: 'var(--tv-bull)' },
  MISMATCH:    { bg: 'var(--tv-bear-tint)', border: 'var(--tv-bear)', fg: 'var(--tv-bear)' },
  NO_DATA:     { bg: 'var(--tv-border-lt)', border: 'var(--tv-text-muted)', fg: 'var(--tv-text-muted)' },
  NOT_CHECKED: { bg: 'var(--tv-border-lt)', border: 'var(--tv-text-muted)', fg: 'var(--tv-text-muted)' },
};

/* ── Formatters (matching StrategyHealthPanel) ─────────────────────── */

function fmtAge(min) {
  if (min == null) return '—';
  if (min < 1)   return `${Math.round(min * 60)}s`;
  if (min < 60)  return `${min.toFixed(1)}m`;
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  if (h < 24)    return `${h}h ${m}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

function fmtET(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
    const time = d.toLocaleTimeString('en-GB', {
      timeZone: 'America/New_York', hour12: false,
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    return `${date} ${time}`;
  } catch { return iso; }
}

/* ── Reusable pills ───────────────────────────────────────────────── */

const pillBase = {
  display: 'inline-block', padding: '3px 10px', borderRadius: 4,
  fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
  textTransform: 'uppercase',
};

function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.DOWN;
  return (
    <span style={{ ...pillBase, background: s.bg, border: `1px solid ${s.border}`, color: s.fg }}>
      {status}
    </span>
  );
}

function VerdictBadge({ verdict, coverage }) {
  const v = VERDICT_STYLE[verdict] || VERDICT_STYLE.NOT_CHECKED;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ ...pillBase, background: v.bg, border: `1px solid ${v.border}`, color: v.fg }}>
        {verdict || 'N/A'}
      </span>
      {coverage === 'ENVELOPE' && (
        <span style={{ fontSize: 9, color: 'var(--tv-text-muted)', fontStyle: 'italic' }}>
          trigger trusted
        </span>
      )}
    </span>
  );
}

/* ── Expanded detail panels ───────────────────────────────────────── */

const detailCell = { padding: '6px 10px', fontSize: 11, borderBottom: '1px solid var(--tv-border-lt)' };
const detailHeader = {
  ...detailCell,
  fontWeight: 700, fontSize: 9, color: 'var(--tv-text-muted)',
  textTransform: 'uppercase', letterSpacing: '0.08em',
  background: 'var(--tv-surface-hi)',
};
const diffHighlight = { background: 'var(--tv-bear-tint)', color: 'var(--tv-bear)' };

function FullCoverageDetail({ books }) {
  if (!books || books.length === 0) {
    return <div style={{ padding: 12, fontSize: 11, color: 'var(--tv-text-muted)' }}>No book data available.</div>;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {books.map((book) => (
        <div key={book.book}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--tv-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
            {book.book} Book
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead>
              <tr>
                <th style={detailHeader}>Field</th>
                <th style={detailHeader}>Expected</th>
                <th style={detailHeader}>Stored</th>
                <th style={detailHeader}>Match</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={detailCell}>Overall</td>
                <td style={{ ...detailCell, fontFamily: 'Roboto Mono, monospace' }}>{String(book.expected)}</td>
                <td style={{ ...detailCell, fontFamily: 'Roboto Mono, monospace' }}>{String(book.stored)}</td>
                <td style={detailCell}>
                  <span style={{ color: book.match ? 'var(--tv-bull)' : 'var(--tv-bear)', fontWeight: 700 }}>
                    {book.match ? '✓' : '✗'}
                  </span>
                </td>
              </tr>
              {book.diffs && book.diffs.length > 0 && book.diffs.map((diff, i) => (
                <tr key={i} style={diffHighlight}>
                  <td style={{ ...detailCell, fontWeight: 600 }}>{diff.field}</td>
                  <td style={{ ...detailCell, fontFamily: 'Roboto Mono, monospace' }}>{String(diff.expected)}</td>
                  <td style={{ ...detailCell, fontFamily: 'Roboto Mono, monospace' }}>{String(diff.stored)}</td>
                  <td style={detailCell}>
                    <span style={{ color: 'var(--tv-bear)', fontWeight: 700 }}>✗</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

function EnvelopeCoverageDetail({ checks }) {
  if (!checks || checks.length === 0) {
    return <div style={{ padding: 12, fontSize: 11, color: 'var(--tv-text-muted)' }}>No checks data available.</div>;
  }
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
      <thead>
        <tr>
          <th style={detailHeader}>Field</th>
          <th style={detailHeader}>Expected</th>
          <th style={detailHeader}>Match</th>
        </tr>
      </thead>
      <tbody>
        {checks.map((check, i) => (
          <tr key={i} style={check.match === false ? diffHighlight : undefined}>
            <td style={{ ...detailCell, fontWeight: 600 }}>{check.field}</td>
            <td style={{ ...detailCell, fontFamily: 'Roboto Mono, monospace' }}>{String(check.expected)}</td>
            <td style={detailCell}>
              <span style={{ color: check.match ? 'var(--tv-bull)' : 'var(--tv-bear)', fontWeight: 700 }}>
                {check.match ? '✓' : '✗'}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CorrectnessDetail({ correctness }) {
  if (!correctness) return null;
  const { verdict, coverage, books, checks, note } = correctness;

  if (verdict === 'NO_DATA' || verdict === 'NOT_CHECKED') {
    return (
      <div style={{ padding: 12, fontSize: 11, color: 'var(--tv-text-muted)', fontStyle: 'italic' }}>
        {verdict === 'NO_DATA' ? 'No correctness data available — probe snapshot not yet relayed.' : 'Correctness check has not been run.'}
        {note && <span style={{ display: 'block', marginTop: 4 }}>{note}</span>}
      </div>
    );
  }

  return (
    <div style={{ padding: '12px 14px' }}>
      {note && (
        <div style={{ marginBottom: 10, padding: '6px 10px', borderRadius: 4, background: 'var(--tv-surface-hi)', fontSize: 11, color: 'var(--tv-text-dim)' }}>
          {note}
        </div>
      )}
      {coverage === 'FULL' && <FullCoverageDetail books={books} />}
      {coverage === 'ENVELOPE' && <EnvelopeCoverageDetail checks={checks} />}
    </div>
  );
}

/* ── Main panel ───────────────────────────────────────────────────── */

export default function HealthCheckPanel() {
  const [data, setData] = useState({
    strategies: [],
    as_of: null,
    session: null,
    fresh_window_minutes: null,
    checked_at: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);

  const load = useCallback(async () => {
    try {
      // Try overview endpoint first (single call)
      const overviewRes = await authFetch('/strategies/api/admin/health/overview/');
      if (overviewRes.ok) {
        const overview = await overviewRes.json();
        // Normalise: overview returns strategies with nested liveness + correctness blocks
        const strategies = (overview.strategies || []).map(s => ({
          id: s.id,
          // Liveness fields
          table: s.liveness?.table || s.table,
          status: s.liveness?.status || s.status,
          last_signal_ts: s.liveness?.last_signal_ts || s.last_signal_ts,
          age_minutes: s.liveness?.age_minutes ?? s.age_minutes,
          today_count: s.liveness?.today_count ?? s.today_count,
          last_24h_count: s.liveness?.last_24h_count ?? s.last_24h_count,
          mode: s.liveness?.mode || s.mode,
          paused: s.liveness?.paused ?? s.paused,
          exists: s.liveness?.exists ?? s.exists,
          liveness_note: s.liveness?.liveness_note || s.liveness_note,
          latest_decision: s.liveness?.latest_decision || s.latest_decision,
          // Correctness fields
          correctness: s.correctness || null,
        }));
        setData({
          strategies,
          as_of: overview.as_of,
          session: overview.session,
          fresh_window_minutes: overview.fresh_window_minutes,
          checked_at: overview.checked_at,
        });
        setError('');
        return;
      }

      // Fallback: parallel calls
      const [livenessRes, correctnessRes] = await Promise.all([
        authFetch('/strategies/api/admin/health/strategies/latest'),
        authFetch('/strategies/api/admin/health/correctness/'),
      ]);

      if (!livenessRes.ok) {
        setError(`Liveness HTTP ${livenessRes.status}`);
        return;
      }

      const liveness = await livenessRes.json();
      let correctnessMap = {};

      if (correctnessRes.ok) {
        const correctnessData = await correctnessRes.json();
        (correctnessData.results || []).forEach(r => {
          correctnessMap[r.id] = r;
        });
      }

      const strategies = (liveness.strategies || []).map(s => ({
        ...s,
        correctness: correctnessMap[s.id] || null,
      }));

      setData({
        strategies,
        as_of: liveness.as_of,
        session: liveness.session,
        fresh_window_minutes: liveness.fresh_window_minutes,
        checked_at: null,
      });
      setError('');
    } catch (e) {
      setError('Connection failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  const items = data.strategies || [];
  const counts = items.reduce((acc, s) => { acc[s.status] = (acc[s.status] || 0) + 1; return acc; }, {});

  const toggleRow = (id) => {
    setExpandedRow(prev => prev === id ? null : id);
  };

  return (
    <div style={{ padding: '0' }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 18, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--tv-text)' }}>Health Check</h2>
        <span style={{ fontSize: 11, color: 'var(--tv-text-muted)', fontFamily: 'Roboto Mono, monospace' }}>
          {data.as_of ? `as of ${fmtET(data.as_of)} ET` : '—'} · session {data.session || '—'} · stale &gt; {data.fresh_window_minutes ?? '—'}m
        </span>
        <button type="button" onClick={load} style={{
          marginLeft: 'auto', padding: '6px 14px', fontSize: 12, fontWeight: 600,
          border: '1px solid var(--tv-border)', borderRadius: 6, cursor: 'pointer',
          background: 'var(--tv-surface)', color: 'var(--tv-blue)',
        }}>Refresh</button>
      </div>

      {/* Status summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 20 }}>
        {['HEALTHY', 'STALE', 'DOWN', 'PAUSED'].map(st => {
          const s = STATUS_STYLE[st];
          return (
            <div key={st} style={{
              padding: '14px 16px', borderRadius: 6,
              background: 'var(--tv-surface)',
              border: `1px solid var(--tv-border)`,
              borderLeft: `4px solid ${s.border}`,
            }}>
              <div style={{ fontSize: 10, color: 'var(--tv-text-muted)', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{st}</div>
              <div style={{ fontSize: 24, color: s.fg, fontFamily: 'Roboto Mono, monospace', marginTop: 4, fontWeight: 700 }}>{counts[st] || 0}</div>
            </div>
          );
        })}
      </div>

      {error && (
        <div style={{ padding: 12, marginBottom: 14, background: 'var(--tv-bear-tint)', border: '1px solid var(--tv-bear)', borderRadius: 6, color: 'var(--tv-bear)', fontSize: 12 }}>
          {error}
        </div>
      )}

      {/* Strategy table */}
      <div style={{ background: 'var(--tv-surface)', border: '1px solid var(--tv-border)', borderRadius: 8, overflowX: 'auto', overflowY: 'hidden', WebkitOverflowScrolling: 'touch' }}>
        <table style={{ width: '100%', minWidth: 860, borderCollapse: 'collapse', fontSize: 12 }}>
          <thead style={{ background: 'var(--tv-surface-hi)' }}>
            <tr>
              {['Strategy', 'Last Signal (ET)', 'Age', 'Today', '24h', 'Mode', 'Status', 'Correctness'].map(h => (
                <th key={h} style={{
                  padding: '10px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700,
                  color: 'var(--tv-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em',
                  borderBottom: '1px solid var(--tv-border)',
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} style={{ padding: 20, textAlign: 'center', color: 'var(--tv-text-muted)' }}>Loading…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={8} style={{ padding: 20, textAlign: 'center', color: 'var(--tv-text-muted)' }}>No strategies returned</td></tr>
            )}
            {items.map(s => {
              const isExpanded = expandedRow === s.id;
              const correctness = s.correctness || {};
              const hasDetail = correctness.verdict && correctness.verdict !== 'NO_DATA' && correctness.verdict !== 'NOT_CHECKED';

              return (
                <React.Fragment key={s.id}>
                  <tr
                    onClick={() => toggleRow(s.id)}
                    style={{
                      borderTop: '1px solid var(--tv-border-lt)',
                      cursor: 'pointer',
                      background: isExpanded ? 'var(--tv-surface-hi)' : undefined,
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => { if (!isExpanded) e.currentTarget.style.background = 'var(--tv-surface-hi)'; }}
                    onMouseLeave={(e) => { if (!isExpanded) e.currentTarget.style.background = ''; }}
                  >
                    {/* Strategy name */}
                    <td style={{ padding: '10px 14px', fontWeight: 700, color: 'var(--tv-text)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{
                          display: 'inline-block', width: 16, textAlign: 'center',
                          fontSize: 10, color: 'var(--tv-text-muted)',
                          transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                          transition: 'transform 0.15s',
                        }}>▶</span>
                        <div>
                          {s.id}
                          {s.liveness_note && (
                            <div style={{ fontSize: 10, color: 'var(--tv-text-muted)', fontWeight: 400, fontStyle: 'italic', marginTop: 2, maxWidth: 220 }}>
                              {s.liveness_note}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    {/* Last Signal */}
                    <td style={{ padding: '10px 14px', fontFamily: 'Roboto Mono, monospace', color: 'var(--tv-text-dim)' }}>{fmtET(s.last_signal_ts)}</td>
                    {/* Age */}
                    <td style={{
                      padding: '10px 14px', fontFamily: 'Roboto Mono, monospace',
                      color: s.age_minutes != null && s.age_minutes > (data.fresh_window_minutes || 60) ? 'var(--tv-bear)' : 'var(--tv-text-dim)',
                    }}>
                      {fmtAge(s.age_minutes)}
                    </td>
                    {/* Today */}
                    <td style={{ padding: '10px 14px', fontFamily: 'Roboto Mono, monospace', color: 'var(--tv-text)' }}>{s.today_count}</td>
                    {/* 24h */}
                    <td style={{ padding: '10px 14px', fontFamily: 'Roboto Mono, monospace', color: 'var(--tv-text)' }}>{s.last_24h_count}</td>
                    {/* Mode */}
                    <td style={{ padding: '10px 14px', fontFamily: 'Roboto Mono, monospace', color: 'var(--tv-text-dim)' }}>{s.mode || '—'}</td>
                    {/* Status pill */}
                    <td style={{ padding: '10px 14px' }}><StatusPill status={s.status} /></td>
                    {/* Correctness badge */}
                    <td style={{ padding: '10px 14px' }}>
                      <VerdictBadge verdict={correctness.verdict} coverage={correctness.coverage} />
                    </td>
                  </tr>
                  {/* Expanded detail row */}
                  {isExpanded && (
                    <tr>
                      <td
                        colSpan={8}
                        style={{
                          padding: 0,
                          background: 'var(--tv-surface)',
                          borderTop: '1px dashed var(--tv-border)',
                          borderBottom: '1px solid var(--tv-border-lt)',
                        }}
                      >
                        <div style={{ padding: '14px 20px 18px 46px' }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--tv-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
                            Correctness Detail
                            {correctness.coverage && (
                              <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, marginLeft: 8, fontStyle: 'italic' }}>
                                ({correctness.coverage === 'FULL' ? 'full ground truth' : 'envelope / trigger only'})
                              </span>
                            )}
                          </div>
                          {hasDetail ? (
                            <div style={{ background: 'var(--tv-surface-hi)', border: '1px solid var(--tv-border)', borderRadius: 6, overflow: 'hidden' }}>
                              <CorrectnessDetail correctness={correctness} />
                            </div>
                          ) : (
                            <CorrectnessDetail correctness={correctness} />
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
